// Little films drawn by the game itself: the camera glides over real places (with the townsfolk going about
// their day) while captions fade in between letterbox bars. The title sequence plays before you sign in; the
// intro plays once for a new character, before they wake up at home.
//
// The main loop asks camera() each frame while nobody is playing (no character in the world yet) and draws that.
import type { SkyOverride } from '../../../shared/time.js';

/** One camera move: from one spot to another (tiles), zooming between two levels, over `ms`. */
export interface Shot { zone: string; from: [number, number]; to: [number, number]; zoom: [number, number]; ms: number; caption?: string; hour?: number }
export interface Film { shots: Shot[]; /** After the last shot: keep drifting on the last one (the title card) or finish. */ hold?: boolean }

const $ = (id: string) => document.getElementById(id)!;
const ease = (k: number) => k < 0.5 ? 2 * k * k : 1 - (-2 * k + 2) ** 2 / 2;
const FADE = 650;

export const TITLE: Film = { hold: true, shots: [
  { zone: 'town', from: [7, 6], to: [31, 7], zoom: [0.85, 0.95], ms: 6500, hour: 17.2, caption: 'Past the Whisperwood, where the river bends…' },
  { zone: 'town', from: [19.5, 19], to: [19.5, 15.5], zoom: [1.15, 1.7], ms: 5200, hour: 17.4, caption: '…there is a little town that always has room for one more.' },
  { zone: 'harbor', from: [6, 14], to: [26, 12], zoom: [1.05, 1.15], ms: 5600, hour: 18.1, caption: 'Its people bake, fish, mend and wander.' },
  { zone: 'town', from: [12, 10], to: [26, 16], zoom: [0.8, 0.8], ms: 60_000, hour: 18.6 },   // under the title card
] };

export const INTRO: Film = { shots: [
  { zone: 'gate', from: [14, 16], to: [14, 6], zoom: [0.9, 1.1], ms: 5200, hour: 5.6, caption: 'Every adventure in Tiny Town starts the same way.' },
  { zone: 'town', from: [31, 22], to: [19.5, 15], zoom: [0.9, 1.25], ms: 5600, hour: 6.1, caption: 'The bakers light their ovens. The fountain wakes up. Somebody is always humming.' },
  { zone: 'cottages', from: [6, 11], to: [24, 9], zoom: [1.1, 1.5], ms: 5600, hour: 6.6, caption: 'And down on Cottage Lane, behind a green door…' },
] };

export class Cinema {
  private film: Film | null = null; private i = 0; private t0 = 0; private done: (() => void) | null = null;
  /** Called when the camera moves to another place (the main loop switches what it draws). */
  onZone: (zone: string) => void = () => {};
  /** Called with the time of day a shot wants (null: back to the real sky). */
  onSky: (o: SkyOverride | null) => void = () => {};
  /** Keys and buttons skip ahead. */
  private skip = () => this.next();

  constructor() {
    $('cineSkip').addEventListener('click', e => { e.stopPropagation(); this.finish(); });
  }
  get active(): boolean { return !!this.film; }
  get holding(): boolean { return !!this.film?.hold && this.i === this.film.shots.length - 1; }

  /** Play a film; resolves when it ends (or is skipped). With `skipTo`, start on that shot (seen it already). */
  play(film: Film, skipTo = 0): Promise<void> {
    this.film = film; this.i = Math.min(skipTo, film.shots.length - 1); this.t0 = performance.now();
    $('cine').hidden = false; $('cine').classList.toggle('titled', !!film.hold);
    document.body.classList.add('cinema');
    $('cineLogo').hidden = true;
    this.enter();
    addEventListener('keydown', this.onKey); addEventListener('pointerdown', this.onPointer);
    return new Promise(res => { this.done = res; });
  }
  private onKey = (e: KeyboardEvent) => { if (e.repeat || /INPUT|TEXTAREA/.test((e.target as HTMLElement)?.tagName ?? '')) return; e.preventDefault(); if (e.code === 'Escape') this.finish(); else this.skip(); };
  private onPointer = (e: PointerEvent) => { if ((e.target as HTMLElement)?.closest('#gate, .cine-skip')) return; this.skip(); };
  /** A pad button: same as a key. */
  pad(): void { if (this.film) this.skip(); }

  private enter(): void {
    const s = this.film!.shots[this.i];
    this.onZone(s.zone); this.onSky(s.hour != null ? { hour: s.hour, weather: 'clear' } : null);
    const cap = $('cineCap'); cap.classList.remove('on'); cap.textContent = s.caption ?? '';
    if (s.caption) setTimeout(() => cap.classList.add('on'), 350);
    if (this.holding) { $('cineLogo').hidden = false; $('cine').classList.add('logo'); $('cineSkip').hidden = true; }
    else { $('cine').classList.remove('logo'); $('cineSkip').hidden = false; }
    this.t0 = performance.now();
  }
  private next(): void {
    if (!this.film) return;
    if (this.holding) return this.finish();
    this.i++;
    if (this.i >= this.film.shots.length) return this.finish();
    this.enter();
  }
  /** Stop: the title card stays drifting behind the sign-in cards (`keepBackdrop`); anything else ends. */
  finish(keepBackdrop = !!this.film?.hold): void {
    if (!this.film) return;
    if (this.film.hold && !this.holding && keepBackdrop) { this.i = this.film.shots.length - 1; this.enter(); return; }   // skipping the title: straight to the title card
    removeEventListener('keydown', this.onKey); removeEventListener('pointerdown', this.onPointer);
    $('cineCap').classList.remove('on'); $('cineSkip').hidden = true;
    const d = this.done; this.done = null;
    if (keepBackdrop) { $('cine').classList.add('backdrop'); this.backdrop = true; }
    else { this.film = null; $('cine').hidden = true; $('cine').classList.remove('backdrop', 'logo', 'titled'); document.body.classList.remove('cinema'); this.onSky(null); this.backdrop = false; }
    d?.();
  }
  /** The title card is drifting behind the sign-in screens. */
  backdrop = false;
  /** Done with the backdrop too (playing, or the creator opened). */
  stop(): void { if (this.film) { this.backdrop = false; this.finish(false); } }

  /** Where the camera is right now (pixels) and how far in, plus how dark the fade between shots is. */
  camera(now: number, ts: number): { x: number; y: number; zoom: number; fade: number } | null {
    const f = this.film; if (!f) return null;
    const s = f.shots[this.i], t = now - this.t0;
    if (t >= s.ms && !this.holding) { this.next(); return this.camera(now, ts); }
    const k = ease(Math.min(1, t / s.ms));
    const fade = this.holding ? Math.max(0, 1 - t / FADE) : Math.max(0, 1 - t / FADE, (t - (s.ms - FADE)) / FADE);
    return { x: (s.from[0] + (s.to[0] - s.from[0]) * k) * ts, y: (s.from[1] + (s.to[1] - s.from[1]) * k) * ts, zoom: s.zoom[0] + (s.zoom[1] - s.zoom[0]) * k, fade: Math.min(1, fade) };
  }
}
